/**
 * THE linear progress motion — one source of truth for both renderers:
 *   - web    → `AnimatedFill.tsx` steps {@link springStep} on rAF (writing
 *              transforms only) and runs the indeterminate loop through WAAPI;
 *   - native → `AnimatedFill.native.tsx` runs the SAME critically damped spring
 *              through Reanimated `withSpring` ({@link springConfigFor}) and
 *              evaluates the same worklets on the UI thread.
 *
 * Motion rules (design research 2026-10, the upload screens show ONLY the bar):
 *   - incoming values are smoothed by a critically damped spring — velocity
 *     carries across updates (no stepping kink), and it never overshoots, so
 *     the fill can never run ahead of real progress;
 *   - a `live` bar (a running task) never moves backwards; a value of 0 is a
 *     new task and snaps back;
 *   - NO fake progress: a stalled task leaves the fill where it is — liveness
 *     comes from the decorative sheen, never from creeping;
 *   - 100 switches to a stiffer spring (a fast glide to full), then holds;
 *   - everything is transform / opacity; reduced motion snaps and drops loops.
 *
 * Units: fractions of the track (0–1), times in ms, `omega` in rad/s.
 */

/** Settle time ≈ 2.9 × `smoothMs` (to within 2%) for a critically damped spring. */
export const PROGRESS_MOTION = {
  smoothMs: {
    /** Plain bars (study / quiz steps): settles in ≈ 320 ms — the old 300 ms glide. */
    step: 110,
    /** `live` bars (uploads, processing): ≈ 0.9 s, so sparse updates blend into one motion. */
    follow: 320,
    /** Any bar heading to 100%: ≈ 260 ms — finishing reads as decisive. */
    complete: 90,
  },
  /** Snap to the target once this close (fraction) and this slow (fraction / s). */
  rest: { delta: 0.0005, velocity: 0.002 },
  /** Indeterminate ↔ determinate cross-fade. */
  handoffMs: 220,
  indeterminate: {
    /** One pass of the travelling segment (Material: 2 s; calmer single segment). */
    periodMs: 1700,
    /** Segment width, fraction of the track. */
    segment: 0.38,
    /** Ease-in-out cubic: leaves gently, crosses briskly, arrives gently. */
    easing: [0.65, 0, 0.35, 1] as const,
    /** Reduced motion: a static full-width fill at this opacity (no loop). */
    reducedOpacity: 0.4,
  },
  sheen: {
    /** Time the highlight takes to cross the filled part. */
    sweepMs: 1500,
    /** Pause between passes — keeps it a glint, not a strobe. */
    restMs: 1300,
    /** Highlight width, fraction of the track. */
    band: 0.3,
    /** Peak alpha of the highlight (`fg-on-primary`, stable in both themes). */
    peakOpacity: 0.32,
    /** Fade when the sheen turns off (completion / prop off). */
    fadeMs: 200,
  },
} as const;

export type ProgressSmoothing = keyof typeof PROGRESS_MOTION.smoothMs;

/** The CSS form of the indeterminate curve (web WAAPI / native `Easing.bezier`). */
export const PROGRESS_INDETERMINATE_EASING_CSS = `cubic-bezier(${PROGRESS_MOTION.indeterminate.easing.join(', ')})`;

/** Full sheen cycle (sweep + rest). */
export const PROGRESS_SHEEN_PERIOD_MS =
  PROGRESS_MOTION.sheen.sweepMs + PROGRESS_MOTION.sheen.restMs;

/** Clamp a percent (any number) to a 0–1 fraction. */
export function toProgressFraction(percent: number): number {
  'worklet';
  if (!Number.isFinite(percent)) return 0;
  return Math.min(1, Math.max(0, percent / 100));
}

/**
 * Which spring a bar uses toward `target`: completion beats everything, then
 * `live` follows, else the snappy step.
 */
export function smoothingFor(target: number, live: boolean): ProgressSmoothing {
  'worklet';
  if (target >= 1) return 'complete';
  return live ? 'follow' : 'step';
}

/** Natural frequency (rad/s) of the critically damped spring for a smoothing. */
export function omegaFor(smoothing: ProgressSmoothing): number {
  'worklet';
  return 2000 / PROGRESS_MOTION.smoothMs[smoothing];
}

/**
 * The same spring as a Reanimated `withSpring` config — mass 1, k = ω²,
 * c = 2ω (critical), overshoot clamped. Kept here so the web stepper and the
 * native spring can never drift apart.
 */
export function springConfigFor(smoothing: ProgressSmoothing): {
  mass: number;
  stiffness: number;
  damping: number;
  overshootClamping: boolean;
} {
  'worklet';
  const omega = omegaFor(smoothing);
  return {
    mass: 1,
    stiffness: omega * omega,
    damping: 2 * omega,
    overshootClamping: true,
  };
}

/**
 * Where the fill should head when a new value arrives.
 *   - non-`monotonic` (plain bars): straight to the value, either direction;
 *   - `monotonic` (`live` bars): a running max — a lower value is ignored,
 *     except 0, which is a new task and SNAPS back (no rewind animation).
 * `snap` tells the renderer to jump instead of animating.
 */
export function nextProgressTarget(
  previousTarget: number,
  value: number,
  monotonic: boolean,
): { target: number; snap: boolean } {
  'worklet';
  const next = Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
  if (!monotonic) return { target: next, snap: false };
  if (next <= 0) return { target: 0, snap: previousTarget > 0 };
  return { target: Math.max(previousTarget, next), snap: false };
}

export interface SpringState {
  /** Current position (fraction). */
  x: number;
  /** Current velocity (fraction / s). */
  v: number;
}

/**
 * One exact step of a critically damped spring toward `target` (closed form,
 * so it is stable for any frame time). Never overshoots the target, never
 * moves away from it, and snaps to rest inside {@link PROGRESS_MOTION.rest}.
 * Returns the new state and whether it has settled.
 */
export function springStep(
  state: SpringState,
  target: number,
  omega: number,
  dtSeconds: number,
): SpringState & { settled: boolean } {
  'worklet';
  const dt = Math.max(0, dtSeconds);
  const d0 = state.x - target;
  const e = Math.exp(-omega * dt);
  const k = state.v + omega * d0;
  let d = (d0 + k * dt) * e;
  let v = (state.v - omega * k * dt) * e;
  // Overshoot clamp: crossing the target means arriving at it.
  if (d0 !== 0 && Math.sign(d) !== Math.sign(d0) && d !== 0) {
    d = 0;
    v = 0;
  }
  // Never step AWAY from the target (only possible with a stale velocity
  // pointing the other way — e.g. a value that reversed mid-glide).
  if (Math.abs(d) > Math.abs(d0)) {
    d = d0;
    v = 0;
  }
  const { delta, velocity } = PROGRESS_MOTION.rest;
  if (Math.abs(d) < delta && Math.abs(v) < velocity) {
    return { x: target, v: 0, settled: true };
  }
  return { x: target + d, v, settled: false };
}

/**
 * Left edge of the indeterminate segment (track fractions) for an EASED loop
 * phase 0–1: it enters fully hidden on the left and leaves fully past the
 * right edge, so the loop seams invisibly.
 */
export function indeterminateLeft(easedPhase: number): number {
  'worklet';
  const { segment } = PROGRESS_MOTION.indeterminate;
  return -segment + easedPhase * (1 + segment);
}

/**
 * Left edge of the sheen (track fractions) for a LINEAR cycle phase 0–1 over
 * a fill reaching `fill`. The highlight sweeps (ease-in-out sine) from just
 * before the bar's start to just past the fill's visible end, then rests
 * past it — the fill clips it, so "past the end" is invisible. Returns `fill`
 * (hidden) for an empty fill.
 */
export function sheenLeft(linearPhase: number, fill: number): number {
  'worklet';
  const { band, sweepMs } = PROGRESS_MOTION.sheen;
  if (fill <= 0) return 0;
  const t = (((linearPhase % 1) + 1) % 1) * PROGRESS_SHEEN_PERIOD_MS;
  if (t >= sweepMs) return fill;
  const s = 0.5 - 0.5 * Math.cos(Math.PI * (t / sweepMs));
  return -band + s * (fill + band);
}

/**
 * Whether the sheen shows: never under reduced motion, never at 100%;
 * otherwise the `sheen` prop, which defaults to on for running work (`live`).
 */
export function isSheenOn({
  sheen,
  live,
  indeterminate,
  fraction,
  reducedMotion,
}: {
  sheen: boolean | undefined;
  live: boolean;
  indeterminate: boolean;
  fraction: number;
  reducedMotion: boolean;
}): boolean {
  if (reducedMotion || indeterminate || fraction >= 1 || fraction <= 0) return false;
  return sheen ?? live;
}
